import { Expose, Type, Transform } from 'class-transformer';
import { DispositionProcessContactDto } from './disposition-process-contact.dto';

export class DispositionProcessConfigResponseDto {
    /**
     * Disposition process configuration ID
     * @example '123e4567-e89b-12d3-a456-426614174000'
     */
    @Expose()
    id: string;

    /**
     * Client Information Record ID
     * @example '123e4567-e89b-12d3-a456-426614174000'
     */
    @Expose()
    clientInformationRecordId: string;

    /**
     * Product ID reference
     * @example '123e4567-e89b-12d3-a456-426614174001'
     */
    @Expose()
    productId?: string;

    /**
     * QA Doc Package Provider Contact ID
     * @example '123e4567-e89b-12d3-a456-426614174002'
     */
    @Expose()
    qaDocPackageProviderContactId?: string;

    /**
     * Receiving Docs Provider Contact ID
     * @example '123e4567-e89b-12d3-a456-426614174003'
     */
    @Expose()
    receivingDocsProviderContactId?: string;

    /**
     * Temp Data Logger Contact ID
     * @example '123e4567-e89b-12d3-a456-426614174004'
     */
    @Expose()
    tempDataLoggerContactId?: string;

    /**
     * Requires client action plan
     * @example false
     */
    @Expose()
    requiresClientActionPlan?: boolean;

    /**
     * Requires QCO disposition
     * @example true
     */
    @Expose()
    requiresQcoDisposition?: boolean;

    /**
     * Requires client disposition
     * @example false
     */
    @Expose()
    requiresClientDisposition?: boolean;

    /**
     * Is sampling required?
     * @example true
     */
    @Expose()
    samplingRequired?: boolean;

    /**
     * Notes related to sampling
     * @example 'Sampling notes'
     */
    @Expose()
    samplingNotes?: string;

    /**
     * Is packaging inspection performed by Q&C?
     * @example true
     */
    @Expose()
    packagingInspectionByQnc?: boolean;

    /**
     * Comments related to packaging inspection performed by Q&C
     * @example 'Performed by Lynden as specified in Jazz Client Manual'
     */
    @Expose()
    packagingInspectionByQncComments?: string;

    /**
     * Does test sample require datalogger?
     * @example false
     */
    @Expose()
    testSampleRequiresDatalogger?: boolean;

    /**
     * Comments related to test sample datalogger requirements
     * @example 'Yes, for Zepzelca & Rylaze. Jazz Ireland Logistics to arrange shipping confirmatory / ID samples'
     */
    @Expose()
    testSampleRequiresDataloggerComments?: string;

    /**
     * Email for sample requests
     * @example 'samples@example.com'
     */
    @Expose()
    sampleRequestEmailTo?: string;

    /**
     * CC emails for sample requests
     * @example 'cc1@example.com, cc2@example.com'
     */
    @Expose()
    sampleRequestEmailCc?: string;

    /**
     * Subject line format
     * @example 'Release Notification: {ProductName}'
     */
    @Expose()
    releaseNotificationFormat?: string;

    /**
     * Email for approvals
     * @example 'approval@example.com'
     */
    @Expose()
    approvalEmailTo?: string;

    /**
     * CC emails for approvals
     * @example 'cc1@example.com, cc2@example.com'
     */
    @Expose()
    approvalEmailCc?: string;

    /**
     * Notes on disposition/assessment process
     * @example 'General assessment notes'
     */
    @Expose()
    assessmentNotes?: string;

    /**
     * Notes
     * @example 'Follow SOP-123'
     */
    @Expose()
    notes?: string;

    /**
     * Creation timestamp
     * @example '2024-01-15T10:30:00Z'
     */
    @Expose()
    createdAt: Date;

    /**
     * Last update timestamp
     * @example '2024-01-15T10:30:00Z'
     */
    @Expose()
    updatedAt: Date;

    /**
     * Contacts associated with the disposition process configuration
     */
    @Expose({ name: 'contacts' })
    @Type(() => DispositionProcessContactDto)
    @Transform(({ obj }) => {
        if (!obj?.contacts || !Array.isArray(obj.contacts)) {
            return [];
        }
        
        return obj.contacts.map((contact: any) => {
            // If contact.contact exists, it's an entity (first transform)
            if (contact.contact) {
                return {
                    contactId: contact.contactId,
                    contactType: contact.contactType,
                    fullName: contact.contact.fullName,
                    email: contact.contact.email,
                    phone: contact.contact.phone,
                    positionTitle: contact.contact.positionTitle
                };
            }
            
            // If contact.contact doesn't exist, it's already transformed data (second transform)
            return {
                contactId: contact.contactId,
                contactType: contact.contactType,
                fullName: contact.fullName,
                email: contact.email,
                phone: contact.phone,
                positionTitle: contact.positionTitle
            };
        });
    })
    contacts: DispositionProcessContactDto[];
}
